/**
 * 错误日志记录器
 * 用于记录组件加载失败、路由生成失败等错误信息
 *
 * @author system
 * @version 1.0.0
 */

/**
 * 错误日志类型枚举
 */
export const ErrorType = {
  COMPONENT_LOAD_ERROR: 'COMPONENT_LOAD_ERROR',
  ROUTE_GENERATE_ERROR: 'ROUTE_GENERATE_ERROR',
  WEBSOCKET_ERROR: 'WEBSOCKET_ERROR',
  API_ERROR: 'API_ERROR'
}

/**
 * 存储键名
 */
const STORAGE_KEY = 'error_logs'
const MAX_LOGS = 50 // 最多保存50条错误日志

/**
 * 记录错误日志
 *
 * @param {Object} log - 错误日志对象
 * @param {string} log.type - 错误类型，参考 ErrorType 枚举
 * @param {string} [log.path] - 相关路径（如组件路径）
 * @param {string} log.message - 错误消息
 * @param {string} [log.stack] - 错误堆栈
 * @param {Object} [log.extra] - 额外信息
 */
export function logError(log) {
  const errorLog = {
    ...log,
    timestamp: Date.now(),
    time: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href
  }

  // 1. 输出到控制台
  console.error('[ErrorLogger]', errorLog)

  // 2. 存储到 localStorage
  try {
    const logs = getErrorLogs()
    logs.unshift(errorLog)

    // 只保留最近的日志
    const trimmedLogs = logs.slice(0, MAX_LOGS)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedLogs))
  } catch (e) {
    console.warn('[ErrorLogger] 无法保存到localStorage:', e)
  }

  // 3. 如果是生产环境，可以发送到服务器（可选）
  if (import.meta.env.PROD && log.type === ErrorType.COMPONENT_LOAD_ERROR) {
    // 可以在这里添加上报逻辑
    // sendErrorToServer(errorLog)
  }
}

/**
 * 获取所有错误日志
 *
 * @returns {Array} 错误日志数组
 */
export function getErrorLogs() {
  try {
    const logs = localStorage.getItem(STORAGE_KEY)
    return logs ? JSON.parse(logs) : []
  } catch (e) {
    console.warn('[ErrorLogger] 无法读取错误日志:', e)
    return []
  }
}

/**
 * 清除所有错误日志
 */
export function clearErrorLogs() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    console.warn('[ErrorLogger] 无法清除错误日志:', e)
  }
}

/**
 * 获取最近一条指定类型的错误日志
 *
 * @param {string} type - 错误类型
 * @returns {Object|null} 错误日志对象
 */
export function getLastErrorByType(type) {
  const logs = getErrorLogs()
  return logs.find(log => log.type === type) || null
}

/**
 * 获取指定路径的错误日志
 *
 * @param {string} path - 路径
 * @returns {Object|null} 错误日志对象
 */
export function getLastErrorByPath(path) {
  const logs = getErrorLogs()
  return logs.find(log => log.path === path) || null
}

export default {
  ErrorType,
  logError,
  getErrorLogs,
  clearErrorLogs,
  getLastErrorByType,
  getLastErrorByPath
}
